// The players picked to compare (SCENARIOS E3, the audit of 2026-10-17 §2,
// B-375): a tray at the foot of the screen that holds them while a manager
// looks around, and **Compare** to see them side by side.
//
// PICKED WHERE THEY ARE SEEN. A tick on the Players page while picking, and
// **Add to compare** on any player's card, put a player in the tray; Compare
// itself keeps the tray to what it shows, so leaving and coming back finds
// the same players. It was one player from a card and the rest searched for
// one at a time, which is how a manager came to find comparing players hard.
//
// THE APP'S, NOT A SCREEN'S: the tray is in the frame between the screen and
// the phone's tabs, so a screen shrinks to make room for it rather than
// hiding its last rows under it. One tray per league, kept for the tab
// (sessionStorage), as a screen's remembered tab is (UX-39).
//
// IT COMES WHEN CALLED (B-407): the bar shows after an add made somewhere
// else — a card's Add to compare, a tick on Players — and Compare puts it
// away. Compare keeps its players here all the same, so coming back finds
// them; but a comparison made in one press on Compare left a bar at the foot
// of every screen after it, which nobody could account for.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { COMPARE_MAX } from '@fsp/domain';
import { leagueIdFromPath } from '../data/route';
import { Button, IconButton } from '../kit/ui';
import { useTier } from './useTier';

export interface Picked {
  id: string;
  name: string;
}

interface Tray {
  /** The league the tray is for; null outside one. */
  leagueId: string | null;
  picked: readonly Picked[];
  /** The bar is out: something was added since Compare last showed them. */
  shown: boolean;
  has: (id: string) => boolean;
  /** In or out; a full tray takes nobody new. An add brings the bar out. */
  toggle: (p: Picked) => void;
  remove: (id: string) => void;
  /** The tray as Compare shows it. */
  set: (list: readonly Picked[]) => void;
  clear: () => void;
  /** Compare has shown them: the bar goes until the next add. */
  settle: () => void;
  full: boolean;
  /** Compare, with whoever is in the tray, or with these when they were just set. */
  open: (ids?: readonly string[]) => void;
}

const TrayContext = createContext<Tray | null>(null);

const keyOf = (leagueId: string) => `fsp.compare.${leagueId}`;

interface Kept {
  picked: Picked[];
  shown: boolean;
}

const NONE: Kept = { picked: [], shown: false };

function recall(leagueId: string | null): Kept {
  if (leagueId === null) return NONE;
  try {
    const raw = sessionStorage.getItem(keyOf(leagueId));
    if (raw === null) return NONE;
    const parsed: unknown = JSON.parse(raw);
    // A list alone is the tray as it was kept before the bar came when
    // called: its players, the bar put away.
    const list: unknown = Array.isArray(parsed)
      ? parsed
      : typeof parsed === 'object' && parsed !== null
        ? (parsed as { picked?: unknown }).picked
        : null;
    if (!Array.isArray(list)) return NONE;
    const picked = list
      .filter(
        (x): x is Picked =>
          typeof x === 'object' &&
          x !== null &&
          typeof (x as Picked).id === 'string' &&
          typeof (x as Picked).name === 'string',
      )
      .slice(0, COMPARE_MAX);
    const shown = !Array.isArray(parsed) && (parsed as { shown?: unknown }).shown === true;
    return { picked, shown: shown && picked.length > 0 };
  } catch {
    // A private window or blocked storage: a tray for this visit only.
    return NONE;
  }
}

function keep(leagueId: string | null, kept: Kept): void {
  if (leagueId === null) return;
  try {
    if (kept.picked.length === 0) sessionStorage.removeItem(keyOf(leagueId));
    else sessionStorage.setItem(keyOf(leagueId), JSON.stringify(kept));
  } catch {
    // Kept in memory only; nothing to tell anyone.
  }
}

/** Around the whole app: the tray belongs to the league in the address. */
export function CompareTrayProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const leagueId = leagueIdFromPath(pathname);
  const [state, setState] = useState(() => ({ leagueId, ...recall(leagueId) }));
  // Another league's tray when the address moves to another league.
  const { picked, shown } = state.leagueId === leagueId ? state : recall(leagueId);
  useEffect(() => {
    if (state.leagueId !== leagueId) setState({ leagueId, ...recall(leagueId) });
  }, [leagueId, state.leagueId]);

  const update = useCallback(
    (next: (was: Kept) => Kept) =>
      setState((was) => {
        const base = was.leagueId === leagueId ? was : recall(leagueId);
        const value = next({ picked: [...base.picked], shown: base.shown });
        const out = {
          picked: value.picked.slice(0, COMPARE_MAX),
          shown: value.shown && value.picked.length > 0,
        };
        keep(leagueId, out);
        return { leagueId, ...out };
      }),
    [leagueId],
  );

  const tray = useMemo<Tray>(
    () => ({
      leagueId,
      picked,
      shown: shown && picked.length > 0,
      has: (id) => picked.some((p) => p.id === id),
      toggle: (p) =>
        update((was) =>
          was.picked.some((x) => x.id === p.id)
            ? { ...was, picked: was.picked.filter((x) => x.id !== p.id) }
            : was.picked.length >= COMPARE_MAX
              ? was
              : { picked: [...was.picked, p], shown: true },
        ),
      remove: (id) => update((was) => ({ ...was, picked: was.picked.filter((x) => x.id !== id) })),
      set: (list) => update((was) => ({ ...was, picked: [...list] })),
      clear: () => update(() => NONE),
      settle: () => update((was) => ({ ...was, shown: false })),
      full: picked.length >= COMPARE_MAX,
      open: (only) => {
        if (leagueId === null) return;
        const ids = (only ?? picked.map((p) => p.id)).map(encodeURIComponent).join(',');
        navigate(`/l/${leagueId}/compare${ids ? `?players=${ids}` : ''}`);
      },
    }),
    [leagueId, picked, shown, update, navigate],
  );
  return <TrayContext.Provider value={tray}>{children}</TrayContext.Provider>;
}

export function useCompareTray(): Tray {
  const tray = useContext(TrayContext);
  if (tray === null) throw new Error('useCompareTray must be used inside CompareTrayProvider');
  return tray;
}

/** The tray where there is one: a card drawn outside the app's frame has none. */
export function useCompareTrayIfAny(): Tray | null {
  return useContext(TrayContext);
}

/** "J. Chase" from "Ja'Marr Chase": the tray's chips are short. */
function shortName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  return `${parts[0]![0]}. ${parts.slice(1).join(' ')}`;
}

/**
 * The tray itself, in the frame under the screen: titled, so it says what it
 * is (B-407). Nothing while it is empty or put away, and nothing on Compare,
 * which is the tray opened out.
 */
export function CompareTrayBar() {
  const tray = useCompareTray();
  const { pathname } = useLocation();
  const phone = useTier() === 'phone';
  if (tray.leagueId === null || !tray.shown || pathname.endsWith('/compare')) return null;
  const n = tray.picked.length;
  return (
    <section className="fsp-tray" aria-label="Players to compare">
      <div className="fsp-tray-body">
        <span className="fsp-cap fsp-tray-title">
          {n === 1 ? 'To compare · pick one more' : `To compare · ${n} of ${COMPARE_MAX}`}
        </span>
        <div className="fsp-tray-names">
          {tray.picked.map((p) => (
            <span key={p.id} className="fsp-tray-chip">
              <span className="fsp-tray-name">{shortName(p.name)}</span>
              <IconButton
                icon="close"
                label={`Take ${p.name} out of the comparison`}
                variant="quiet"
                size="sm"
                onClick={() => tray.remove(p.id)}
              />
            </span>
          ))}
        </div>
      </div>
      <div className="fsp-tray-actions">
        <Button size="xs" variant="quiet" onClick={tray.clear}>
          Clear
        </Button>
        <Button size="xs" variant="primary" icon="compare" onClick={() => tray.open()}>
          {n === 1 ? 'Compare' : phone ? `Compare ${n}` : `Compare ${n} players`}
        </Button>
      </div>
    </section>
  );
}
